import { LILLY_DRUG_SYMBOL_MAP } from "../ApplicationFormEditor/constants";
import { TEMPLATE_NAMES } from "../ApplicationFormEditor/templateNames";

/**
 * FIELD_MASKING_CONFIG defines display rules per field for each assistance program template.
 *
 * There are 3 possible behaviors for each field:
 *
 * 1. Show as-is (default):
 *    - Simply omit the field from the config, or include it with an empty object.
 *    - The field will be shown using its raw or overridden value.
 *
 * 2. Mask value:
 *    - Keep the field and label visible, but replace the value (e.g. with "--").
 *    - Useful when a value exists but should be hidden from users.
 *    - Use `getValue: () => "--"` or a function that returns a masked output.
 *
 * 3. Hide completely:
 *    - Exclude the field entirely (both label and value).
 *    - Useful when the field shouldn't be shown at all for this template.
 *    - Set `show: false`.
 *
 * 4. Customize field label (optional):
 *    - Use `getLabel: (t) => string` to override the default field label.
 *
 * Example:
 * {
 *   grantTotal: {
 *     getValue: () => "--",
 *     getLabel: (t) => t("patientApplications.max_benefit")
 *   }
 * }
 */

export const FIELD_MASKING_CONFIG = {
  [TEMPLATE_NAMES.BMS_COPAY]: {
    applicationRenewalTimeType: {
      getValue: (value, t) => {
        if (value === 1) {
          return t("assistance_programs.modal.application_renewal_time_type.automatically_renews");
        }
        return value;
      }
    }
  },
  [TEMPLATE_NAMES.PFIZER_FREE_DRUG]: {
    possibleDrugs: { show: false },
    maxFpl: { show: false },
    requiredDiagnosis: { show: false },
    acceptedEnrolls: { show: false },
    patientConsentRequired: { show: false },
    requiredCoverageTypes: { show: false },
    applicationRenewalTimeType: { show: false },
    maxNumberOfUses: { show: false },
    offerExpiration: { show: false },
    pharmacyWebsite: { show: false },
    meta: {
      hideRequiredCoverageTypes: true
    }
  },
  [TEMPLATE_NAMES.REGENERON_COPAY]: {
    patientConsentRequired: { show: false },
    grantTotal: {
      getLabel: (t) => t("patientApplications.max_benefit")
    },
    approvedAmount: {
      getLabel: (t) => t("patientApplications.max_benefit")
    },
    shouldNotCapitalizeConditionFirstLetter: true,
    applicationRenewalTimeType: {
      getValue: (value, t) => {
        if (value === 2) {
          return t("assistance_programs.modal.application_renewal_time_type.regeneron_renewal_time_type");
        }
        return value;
      }
    }
  },
  [TEMPLATE_NAMES.REGENERON_PAP]: {
    patientConsentRequired: { show: false },
    shouldNotCapitalizeConditionFirstLetter: true,
    applicationRenewalTimeType: {
      getValue: (value, t) => {
        if (value === 3) {
          return t("assistance_programs.modal.application_renewal_time_type.regeneron_renewal_time_type");
        }
        return value;
      }
    }
  },
  [TEMPLATE_NAMES.LILLY_COPAY]: {
    drugDisplayName: {
      getValue: (value) => (value ? `${value}${LILLY_DRUG_SYMBOL_MAP[value.toLowerCase()] || ""}` : value)
    },
    associatedDrugs: {
      getValue: (value) =>
        Array.isArray(value) ? value.map((drug) => `${drug}${LILLY_DRUG_SYMBOL_MAP[drug.toLowerCase()] || ""}`) : value
    }
  },
  [TEMPLATE_NAMES.LILLY_COPAY_SIGNATURE]: {
    drugDisplayName: {
      getValue: (value) => (value ? `${value}®` : value)
    },
    associatedDrugs: {
      getValue: (value) => (Array.isArray(value) ? value.map((drug) => `${drug}®`) : value)
    }
  }
};
